/**
 * Modal compacto del webinar sobre el rol Embajador (miércoles 2 de septiembre
 * de 2026; se pospuso desde el 19 de agosto). Segunda pieza de la serie de
 * webinars de AdvocatesPro: el primero
 * (5 de agosto, `advproLaunch.constants.ts`) presentaba la nueva versión; éste
 * profundiza en cómo queda el rol Embajador. **Los dos los ven los admins** —
 * son quienes se capacitan en la nueva versión. El webinar HABLA del embajador,
 * no se le muestra al embajador.
 *
 * Comportamiento (solo admins, y solo en empresas no excluidas):
 *  - CTA "Reserva mi lugar" → ÚNICA forma de descartarlo definitivamente.
 *    Marca permanente en localStorage POR USUARIO.
 *  - Aparece **una vez cada 7 días por persona** (ADV-3347, regla de toda la
 *    familia: ver `specialModalFrequency.constants.ts`). Cerrar (X), "Ahora
 *    no" o clic en el fondo solo cierran la vista: la ventana ya se abrió al
 *    mostrarlo.
 *  - Ventana: desde el deploy hasta el 2 de septiembre INCLUSIVE (fin del día
 *    en México, UTC-6, la TZ más al oeste de la audiencia).
 *
 * Hasta ADV-3347 acá NO había snooze: se insistía en cada carga hasta que el
 * admin se inscribiera o pasara la fecha, "para maximizar el alcance". Fue la
 * experiencia repetitiva que originó el ticket.
 *
 * Por qué localStorage y no DB: el del 5 de agosto descarta en
 * `companies.advpro_launch_dismissed`. Reusar esa columna acá haría que
 * descartar un modal descarte también el otro, y agregar una columna nueva más
 * su endpoint no se justifica para una campaña de 6 días. El costo es que un
 * admin que entre desde otro navegador lo vuelva a ver.
 */

import { isExcludedFromSpecialModals } from 'constants/excludedCompanies.constants';
import {
  SpecialModalUserId,
  wasSpecialModalShownRecently
} from 'constants/specialModalFrequency.constants';

/** Corte de visibilidad: 2-sep-2026 23:59:59 hora México (UTC-6). */
export const ADVPRO_AMBASSADOR_END_DATE = '2026-09-02T23:59:59-06:00';

/** Formulario de ClickUp para reservar lugar en el webinar. */
export const ADVPRO_AMBASSADOR_FORM_URL =
  'https://forms.clickup.com/4662065/f/4e8th-179557/W1YN2QPTKYPM1T62C4';

/** Prefijo de la marca de descarte permanente (se le concatena el id del user). */
const DISMISSED_KEY_PREFIX = 'advpro_ambassador_webinar_dismissed';

/** Key de este modal para la marca de frecuencia de la familia. */
export const ADVPRO_AMBASSADOR_MODAL_KEY = 'advpro_ambassador_webinar';

type UserId = SpecialModalUserId;

const dismissedKey = (userId: UserId): string =>
  `${DISMISSED_KEY_PREFIX}:${userId}`;

/**
 * True si la empresa del subdominio actual está excluida de la convocatoria.
 *
 * La lista de excluidos es compartida por todos los Modales Especiales y vive
 * en `excludedCompanies.constants.ts`. Se mantiene este wrapper para no cambiar
 * la API que ya consume el Layout.
 */
export const isExcludedFromAdvproAmbassadorWebinar = (): boolean =>
  isExcludedFromSpecialModals();

/**
 * Decide si el modal debe mostrarse: campaña vigente + empresa no excluida +
 * admin que todavía no clickeó el CTA + ventana de frecuencia vencida para
 * esta persona.
 */
export const shouldShowAdvproAmbassadorModal = (userId: UserId): boolean => {
  // Sin id no hay forma de recordar el descarte: mostrarlo significaría
  // reaparecer incluso después de que el admin se inscribió.
  if (userId === undefined || userId === null || userId === '') return false;
  if (isExcludedFromAdvproAmbassadorWebinar()) return false;
  if (Date.now() >= new Date(ADVPRO_AMBASSADOR_END_DATE).getTime())
    return false;
  try {
    if (localStorage.getItem(dismissedKey(userId)) === 'true') return false;
  } catch {
    // localStorage inaccesible (modo privado restrictivo): mejor no mostrarlo
    // que mostrarlo en cada carga sin poder recordar el descarte.
    return false;
  }
  if (wasSpecialModalShownRecently(ADVPRO_AMBASSADOR_MODAL_KEY, userId)) {
    return false;
  }
  return true;
};

/** Descarta el modal para siempre para este admin (CTA). */
export const dismissAdvproAmbassadorModal = (userId: UserId): void => {
  try {
    localStorage.setItem(dismissedKey(userId), 'true');
  } catch {
    // Sin almacenamiento disponible no hay nada que persistir.
  }
};
